import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  BarChart2,
  ArrowLeft,
  Heart,
  Droplet,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../context/AuthContext';
import RequesterOverview from './RequesterOverview';
import CreateRequest from './CreateRequest';
import ActiveRequest from './ActiveRequest';
import RequestHistory from './RequestHistory';
import RequesterProfile from './RequesterProfile';
import OverallStats from './OverallStats';
import api from '../../../config/api';

const RequesterDashboard = () => {
  const location = useLocation();
  const { user } = useAuth();

  const [activeRequests, setActiveRequests] = useState([]);
  const [showOverallStats, setShowOverallStats] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalRequests: 0,
      activeRequests: 0,
      completedRequests: 0,
      totalDonors: 0
    },
    recentRequests: [],
    bloodTypeRequirements: [],
    statusDistribution: {
      pending: 0,
      in_progress: 0,
      fulfilled: 0,
      partially_fulfilled: 0,
      cancelled: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current section based on URL
  const getCurrentSection = () => {
    const path = location.pathname;

    if (path === '/requester-dashboard') return 'overview';
    if (path.includes('/requester-dashboard/create')) return 'create';
    if (path.includes('/requester-dashboard/active')) return 'active';
    if (path.includes('/requester-dashboard/history')) return 'history';
    if (path.includes('/requester-dashboard/profile')) return 'profile';
    if (path.includes('/requester-dashboard/stats')) return 'stats';

    return 'overview';
  };

  // Fetch dashboard data when component mounts
  useEffect(() => {
    fetchDashboardData();
  }, [location.pathname]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Use the api client instead of direct axios calls
      const response = await api.get('/dashboard/requester');
      setDashboardData(response.data);

      // Also fetch active requests
      const activeRequestsResponse = await api.get('/requests/me');
      setActiveRequests(activeRequestsResponse.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  // Toggle overall stats view
  const toggleOverallStats = () => {
    setShowOverallStats(!showOverallStats);
  };

  // Render content based on current section
  const renderContent = () => {
    const section = getCurrentSection();

    // Check if we're on the create route first
    if (section === 'create') {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <CreateRequest
            setShowNewRequestForm={() => { }} // Empty function for route-based navigation
            activeRequests={activeRequests}
            setActiveRequests={setActiveRequests}
            onSuccess={fetchDashboardData}
          />
        </motion.div>
      );
    }

    if (showOverallStats || section === 'stats') {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Platform Statistics</h2>
                <p className="text-gray-600 text-xs">Comprehensive overview of BloodHero platform</p>
              </div>
            </div>
            <motion.button
              onClick={() => setShowOverallStats(false)}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 text-sm font-medium transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </motion.button>
          </div>
          <OverallStats />
        </motion.div>
      );
    }

    switch (section) {
      case 'active':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ActiveRequest onStatusChange={fetchDashboardData} />
          </motion.div>
        );
      case 'history':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <RequestHistory />
          </motion.div>
        );
      case 'profile':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <RequesterProfile />
          </motion.div>
        );
      default:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header Section */}
            <div className="mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">
                      Requester Dashboard
                    </h1>
                    <p className="text-gray-600 text-sm">
                      Welcome back, {user?.hospitalName || user?.name || 'Hospital'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.button
                    onClick={() => setShowOverallStats(false)}
                    className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create Request</span>
                  </motion.button>

                  <motion.button
                    onClick={toggleOverallStats}
                    className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>View Statistics</span>
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Quick Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4 border border-red-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Total Requests</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData.stats.totalRequests}</p>
                  </div>
                  <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Active Requests</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData.stats.activeRequests}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 border border-green-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Completed</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData.stats.completedRequests}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 border border-purple-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">Total Donors</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData.stats.totalDonors}</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Main Content */}
            <RequesterOverview
              dashboardStats={dashboardData.stats}
              recentRequests={dashboardData.recentRequests}
              bloodTypeRequirements={dashboardData.bloodTypeRequirements}
              statusDistribution={dashboardData.statusDistribution}
              loading={loading}
              error={error}
            />
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Main Content Container */}
      <div className="relative pt-6 md:pt-8 px-6 pb-6">
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default RequesterDashboard;